// Channel and router tools: create, configure, route and delete, with credentials never shown
import { tool } from "ai"
import { z } from "zod"
import { supabase } from "@/lib/agents/db"
import { ROUTER_WEBHOOK, channelWebhooks } from "@/pages/dev/channels/webhooks"
import type { ApprovalRequest } from "@/lib/assistant/types"

// The channel and router pages listen for this to reload
export const CHANNELS_CHANGED_EVENT = "assistant:channels-changed"
export interface ChannelsChange { table: "channels" | "routers"; id: string; deleted?: boolean }

const announce = (detail: ChannelsChange) => window.dispatchEvent(new CustomEvent(CHANNELS_CHANGED_EVENT, { detail }))

// Same choices as the channel page
const CHANNEL_TYPES = ["telerivet", "whatsapp", "messenger", "instagram", "telegram"] as const
const DEBOUNCE_TYPES = ["none", "debounce", "singleAgentInstance"] as const
// Meta platforms, which can be reached through the shared router webhook
const ROUTED_TYPES = ["whatsapp", "messenger", "instagram"]

const TYPE_LABEL: Record<string, string> = {
  app: "App", telerivet: "Telerivet", whatsapp: "WhatsApp", messenger: "Facebook Messenger", instagram: "Instagram", telegram: "Telegram",
}

// What a router's asset id is, per platform (same hints as the router page)
const ASSET_HINT: Record<string, string> = {
  whatsapp: "the Phone Number ID of the WhatsApp Business number, not the phone number itself",
  messenger: "the ID of the Facebook Page that receives the messages",
  instagram: "the ID of the Instagram professional account",
}

// Columns the tools write; everything else on the row is left alone
const CHANNEL_COLUMNS = [
  "title", "type", "agent", "debounce_type", "debounce_time", "debug", "evaluations",
  "telerivet_apikey", "telerivet_projectid", "telerivet_routeid",
  "whatsapp_phoneid", "whatsapp_token", "answer_via_whatsapp", "settings",
] as const

const shown = (v: unknown) => (v ? "set (hidden)" : "not set")

// ── Arguments ────────────────────────────────────────────────────────────────

const text = (d: string) => z.string().nullable().describe(d)

// Every setting a channel has. Null leaves it as it is; an empty string clears it
const settingFields = {
  debounceType: z.enum(DEBOUNCE_TYPES).nullable()
    .describe("none: answer every message. debounce: wait debounceTime seconds and answer a burst once. singleAgentInstance: one run at a time per contact"),
  debounceTime: z.number().nullable().describe("Seconds, required when debounceType is debounce"),
  debug: z.boolean().nullable(),
  evaluationIds: z.array(z.number()).nullable().describe("Evaluation item ids from channel_options, replacing the current list"),
  telerivetApiKey: text("Telerivet: the project's API key. Required for Telerivet"),
  telerivetProjectId: text("Telerivet: project id"),
  telerivetRouteId: text("Telerivet: route id to send replies through"),
  answerViaWhatsapp: z.boolean().nullable().describe("Telerivet: answer through WhatsApp instead (needs whatsappToken)"),
  whatsappPhoneId: text("WhatsApp: Phone Number ID. Optional when a router points at the channel"),
  whatsappToken: text("WhatsApp: access token. Required for WhatsApp"),
  pageId: text("Messenger/Instagram: the linked Facebook Page ID. Optional for Messenger with a router; Instagram needs it to answer"),
  pageToken: text("Messenger/Instagram: Page access token. Required"),
  botToken: text("Telegram: bot token from BotFather. Required"),
}

interface SettingArgs {
  debounceType?: string | null
  debounceTime?: number | null
  debug?: boolean | null
  evaluationIds?: number[] | null
  telerivetApiKey?: string | null
  telerivetProjectId?: string | null
  telerivetRouteId?: string | null
  answerViaWhatsapp?: boolean | null
  whatsappPhoneId?: string | null
  whatsappToken?: string | null
  pageId?: string | null
  pageToken?: string | null
  botToken?: string | null
}

// The channel row with the arguments applied over it
function applyArgs(base: any, a: SettingArgs & { title?: string | null; type?: string | null; agentId?: number | null }) {
  const row: any = { ...base, settings: { ...(base?.settings ?? {}) } }
  const put = (target: any, key: string, v: unknown) => {
    if (v === null || v === undefined) return
    target[key] = typeof v === "string" ? (v.trim() || null) : v
  }
  put(row, "title", a.title)
  put(row, "type", a.type)
  put(row, "agent", a.agentId)
  put(row, "debounce_type", a.debounceType)
  put(row, "debounce_time", a.debounceTime)
  put(row, "debug", a.debug)
  put(row, "evaluations", a.evaluationIds)
  put(row, "telerivet_apikey", a.telerivetApiKey)
  put(row, "telerivet_projectid", a.telerivetProjectId)
  put(row, "telerivet_routeid", a.telerivetRouteId)
  put(row, "answer_via_whatsapp", a.answerViaWhatsapp)
  put(row, "whatsapp_phoneid", a.whatsappPhoneId)
  put(row, "whatsapp_token", a.whatsappToken)
  put(row.settings, "page_id", a.pageId)
  put(row.settings, "page_token", a.pageToken)
  put(row.settings, "bot_token", a.botToken)
  return row
}

// The channel page's validation rules
export function channelProblems(row: any): string[] {
  const p: string[] = []
  const type = row.type
  if (!row.title?.trim()) p.push("title is required.")
  if (!CHANNEL_TYPES.includes(type)) p.push(`type must be one of: ${CHANNEL_TYPES.join(", ")}.`)
  if (row.agent == null) p.push("agentId is required: the agent that answers this channel.")
  if (row.debounce_type && !DEBOUNCE_TYPES.includes(row.debounce_type)) p.push(`debounceType must be one of: ${DEBOUNCE_TYPES.join(", ")}.`)
  if (row.debounce_type === "debounce" && !(row.debounce_time > 0)) p.push("debounceTime (seconds, above 0) is required when debounceType is debounce.")
  if (type === "telerivet" && !row.telerivet_apikey) p.push("A Telerivet channel needs telerivetApiKey.")
  if ((type === "whatsapp" || (type === "telerivet" && row.answer_via_whatsapp)) && !row.whatsapp_token) p.push("whatsappToken is required to answer through WhatsApp.")
  if ((type === "messenger" || type === "instagram") && !row.settings?.page_token) p.push("pageToken (the Page access token) is required for Messenger and Instagram.")
  if (type === "telegram" && !row.settings?.bot_token) p.push("A Telegram channel needs botToken.")
  return p
}

// What's still needed before the channel receives messages
function setupSteps(c: any, routers: any[]): string[] {
  const hooks = channelWebhooks(c.id)
  const routed = routers.length > 0
  const steps: string[] = []
  switch (c.type) {
    case "telerivet":
      steps.push(`In the Telerivet project, send incoming messages to this channel's webhook (${hooks.prod} for production, ${hooks.qa} for QA).`)
      break
    case "whatsapp":
      steps.push(routed
        ? `Reached through the shared Meta webhook by router asset ${routers.map(r => r.asset_id).join(", ")}.`
        : "Not reachable yet: add a router with the number's Phone Number ID (router_create), or set this channel's own webhook as the callback in the Meta app.")
      break
    case "messenger":
      steps.push(routed
        ? `Reached through the shared Meta webhook by router asset ${routers.map(r => r.asset_id).join(", ")}. The Page must be connected to the Signpost Meta app.`
        : "Not reachable yet: add a router with the Facebook Page ID (router_create). The Page must be connected to the Signpost Meta app.")
      break
    case "instagram":
      if (!c.settings?.page_id) steps.push("Set pageId: Instagram replies are sent through the linked Facebook Page.")
      steps.push(routed
        ? `Reached through the shared Meta webhook by router asset ${routers.map(r => r.asset_id).join(", ")}.`
        : "Not reachable yet: add a router with the Instagram professional account ID (router_create).")
      break
    case "telegram":
      steps.push("Point the bot at this channel with channel_connect_telegram, then check it with channel_telegram_status.")
      break
  }
  if (c.debug) steps.push("Debug is on.")
  return steps
}

function describeChannel(c: any, agentTitle: string | undefined, routers: any[]) {
  return {
    id: c.id,
    title: c.title,
    type: c.type,
    typeLabel: TYPE_LABEL[c.type] ?? c.type,
    agentId: c.agent,
    agentTitle: agentTitle ?? null,
    debounceType: c.debounce_type ?? "none",
    debounceTime: c.debounce_time ?? null,
    debug: !!c.debug,
    evaluationIds: c.evaluations ?? [],
    telerivetApiKey: c.type === "telerivet" ? shown(c.telerivet_apikey) : undefined,
    telerivetProjectId: c.telerivet_projectid ?? undefined,
    telerivetRouteId: c.telerivet_routeid ?? undefined,
    answerViaWhatsapp: c.type === "telerivet" ? !!c.answer_via_whatsapp : undefined,
    whatsappPhoneId: c.whatsapp_phoneid ?? undefined,
    whatsappToken: c.type === "whatsapp" || c.answer_via_whatsapp ? shown(c.whatsapp_token) : undefined,
    pageId: c.settings?.page_id ?? undefined,
    pageToken: c.type === "messenger" || c.type === "instagram" ? shown(c.settings?.page_token) : undefined,
    botToken: c.type === "telegram" ? shown(c.settings?.bot_token) : undefined,
    routers: routers.map(r => ({ id: r.id, title: r.title, assetId: r.asset_id })),
    webhook: channelWebhooks(c.id),
    page: `/settings/channels/${c.id}`,
    setup: setupSteps(c, routers),
  }
}

// Field by field, with credentials hidden, for the approval card
function summarize(row: any): string {
  const lines = [
    `Title: ${row.title ?? ""}`,
    `Type: ${TYPE_LABEL[row.type] ?? row.type ?? ""}`,
    `Agent: ${row.agent ?? ""}`,
    `Debounce: ${row.debounce_type ?? "none"}${row.debounce_type === "debounce" ? ` (${row.debounce_time}s)` : ""}`,
    `Debug: ${row.debug ? "on" : "off"}`,
    `Evaluations: ${(row.evaluations ?? []).join(", ") || "none"}`,
  ]
  if (row.type === "telerivet") {
    lines.push(`Telerivet API key: ${shown(row.telerivet_apikey)}`, `Telerivet project: ${row.telerivet_projectid ?? ""}`, `Telerivet route: ${row.telerivet_routeid ?? ""}`, `Answer via WhatsApp: ${row.answer_via_whatsapp ? "yes" : "no"}`)
  }
  if (row.type === "whatsapp" || row.answer_via_whatsapp) lines.push(`WhatsApp phone id: ${row.whatsapp_phoneid ?? ""}`, `WhatsApp token: ${shown(row.whatsapp_token)}`)
  if (row.type === "messenger" || row.type === "instagram") lines.push(`Page id: ${row.settings?.page_id ?? ""}`, `Page token: ${shown(row.settings?.page_token)}`)
  if (row.type === "telegram") lines.push(`Bot token: ${shown(row.settings?.bot_token)}`)
  return lines.join("\n")
}

const pick = (row: any) => Object.fromEntries(CHANNEL_COLUMNS.map(k => [k, row[k] ?? null]))

// ── Module ───────────────────────────────────────────────────────────────────

export interface ChannelsContext {
  teamId?: string
  ask: (req: ApprovalRequest) => Promise<boolean>
}

export function makeChannelsTools(ctx: ChannelsContext) {
  const team = ctx.teamId

  async function loadChannel(id: string) {
    const { data, error } = await supabase.from("channels").select("*").eq("id", id).eq("team", team).maybeSingle()
    if (error) throw new Error(error.message)
    return data as any
  }

  async function routersOf(channelIds: string[]) {
    if (!channelIds.length) return []
    const { data, error } = await supabase.from("routers").select("*").in("channel", channelIds)
    if (error) throw new Error(error.message)
    return (data ?? []) as any[]
  }

  async function agentTitles(ids: number[]): Promise<Map<number, string>> {
    const wanted = [...new Set(ids.filter(v => v != null))]
    if (!wanted.length) return new Map()
    const { data } = await supabase.from("agents").select("id, title").in("id", wanted)
    return new Map((data ?? []).map((a: any) => [a.id, a.title]))
  }

  // Agent and evaluation items must be this team's
  async function referenceProblems(row: any): Promise<string[]> {
    const p: string[] = []
    if (row.agent != null) {
      const { data } = await supabase.from("agents").select("id").eq("id", row.agent).eq("team_id", team).maybeSingle()
      if (!data) p.push(`Agent ${row.agent} isn't one of this team's agents. Pick one from channel_options.`)
    }
    const evals: number[] = row.evaluations ?? []
    if (evals.length) {
      const { data } = await supabase.from("eval_items").select("id").eq("team", team).in("id", evals)
      const found = new Set((data ?? []).map((e: any) => e.id))
      const missing = evals.filter(id => !found.has(id))
      if (missing.length) p.push(`Evaluation items ${missing.join(", ")} aren't this team's. Pick from channel_options.`)
    }
    return p
  }

  async function loadRouter(id: string) {
    const { data, error } = await supabase.from("routers").select("*").eq("id", id).eq("team", team).maybeSingle()
    if (error) throw new Error(error.message)
    return data as any
  }

  // A router can only point at one of this team's Meta channels
  async function routableChannel(channelId: string): Promise<{ channel?: any; error?: string }> {
    const channel = await loadChannel(channelId)
    if (!channel) return { error: `No channel ${channelId} in this team.` }
    if (!ROUTED_TYPES.includes(channel.type)) {
      return { error: `Routers only reach WhatsApp, Messenger and Instagram channels; "${channel.title}" is ${TYPE_LABEL[channel.type] ?? channel.type}.` }
    }
    return { channel }
  }

  // Asset ids are unique across every team: the webhook carries no tenant
  async function assetTaken(assetId: string, exceptId?: string): Promise<string | null> {
    const { data } = await supabase.from("routers").select("id, title, team").eq("asset_id", assetId)
    const other = (data ?? []).find((r: any) => r.id !== exceptId)
    if (!other) return null
    return other.team === team
      ? `Asset ${assetId} is already routed by "${other.title}" (${other.id}). Update that router instead.`
      : `Asset ${assetId} is already routed by another team.`
  }

  const uniqueViolation = (error: any) => error?.code === "23505"

  const noTeam = { error: "No team selected." }

  const tools = {
    channel_options: tool({
      description: "What a channel can be set up with: this team's agents, its evaluation items, the channel types and what each one needs. Read before creating or changing a channel.",
      parameters: z.object({}),
      execute: async () => {
        if (!team) return noTeam
        const [agents, evals] = await Promise.all([
          supabase.from("agents").select("id, title").eq("team_id", team).order("title"),
          supabase.from("eval_items").select("id, name").eq("team", team).order("name"),
        ])
        return {
          agents: (agents.data ?? []).map((a: any) => ({ id: a.id, title: a.title })),
          evaluationItems: (evals.data ?? []).map((e: any) => ({ id: e.id, name: e.name })),
          types: {
            telerivet: "SMS and more through Telerivet. Needs telerivetApiKey; telerivetProjectId and telerivetRouteId as used by the project. answerViaWhatsapp also needs whatsappToken.",
            whatsapp: "WhatsApp Cloud API. Needs whatsappToken. whatsappPhoneId is optional when a router points at the channel.",
            messenger: "Facebook Messenger. Needs pageToken. pageId is optional when a router points at the channel.",
            instagram: "Instagram DMs, answered through the linked Facebook Page. Needs pageToken and pageId.",
            telegram: "Telegram bot. Needs botToken.",
          },
          debounceTypes: DEBOUNCE_TYPES,
        }
      },
    }),

    channels_list: tool({
      description: "List this team's channels with their agent, routers and what's still needed to go live.",
      parameters: z.object({}),
      execute: async () => {
        if (!team) return noTeam
        const { data, error } = await supabase.from("channels").select("*").eq("team", team).order("created_at", { ascending: false })
        if (error) return { error: error.message }
        const rows = (data ?? []) as any[]
        const [routers, titles] = await Promise.all([routersOf(rows.map(c => c.id)), agentTitles(rows.map(c => c.agent))])
        return { channels: rows.map(c => describeChannel(c, titles.get(c.agent), routers.filter(r => r.channel === c.id))) }
      },
    }),

    channel_get: tool({
      description: "One channel's full settings (credentials show only as set or not set), its routers, webhook urls and remaining setup steps.",
      parameters: z.object({ id: z.string() }),
      execute: async ({ id }) => {
        if (!team) return noTeam
        const c = await loadChannel(id)
        if (!c) return { error: `No channel ${id} in this team.` }
        const [routers, titles] = await Promise.all([routersOf([id]), agentTitles([c.agent])])
        return describeChannel(c, titles.get(c.agent), routers)
      },
    }),

    channel_create: tool({
      description:
        "Create a channel: how beneficiaries reach an agent (Telerivet, WhatsApp, Messenger, Instagram or Telegram). Always asks the user first. "
        + "Credentials come from the user; never invent them. Returns the webhook urls and the setup steps left.",
      parameters: z.object({
        title: z.string(),
        type: z.enum(CHANNEL_TYPES),
        agentId: z.number().describe("The agent that answers, from channel_options"),
        ...settingFields,
      }),
      execute: async (a) => {
        if (!team) return noTeam
        const row = applyArgs({ debounce_type: "none", debug: false, evaluations: [] }, a)
        const problems = [...channelProblems(row), ...await referenceProblems(row)]
        if (problems.length) return { error: `Nothing was created:\n- ${problems.join("\n- ")}` }
        const ok = await ctx.ask({
          summary: `Create ${TYPE_LABEL[row.type]} channel "${row.title}"`,
          detail: summarize(row) + "\n\nOnce its platform is connected, this agent answers real messages on it.",
        })
        if (!ok) return { error: "Rejected by reviewer." }
        const { data, error } = await supabase.from("channels").insert({ ...pick(row), team } as any).select("*").single()
        if (error) return { error: error.message }
        announce({ table: "channels", id: (data as any).id })
        const titles = await agentTitles([row.agent])
        return { ok: true, created: describeChannel(data, titles.get(row.agent), []) }
      },
    }),

    channel_update: tool({
      description:
        "Change a channel's settings: title, type, agent, debounce, debug, evaluations or credentials. Only the fields you pass change; "
        + "an empty string clears a text field. Always asks the user first. Changing the agent changes who answers live messages.",
      parameters: z.object({
        id: z.string(),
        title: z.string().nullable(),
        type: z.enum(CHANNEL_TYPES).nullable(),
        agentId: z.number().nullable(),
        ...settingFields,
      }),
      execute: async (a) => {
        if (!team) return noTeam
        const before = await loadChannel(a.id)
        if (!before) return { error: `No channel ${a.id} in this team.` }
        const after = applyArgs(before, a)
        const problems = [...channelProblems(after), ...await referenceProblems(after)]
        if (problems.length) return { error: `Nothing was changed:\n- ${problems.join("\n- ")}` }
        const changed = CHANNEL_COLUMNS.filter(k => JSON.stringify(before[k] ?? null) !== JSON.stringify(after[k] ?? null))
        if (!changed.length) return { ok: true, unchanged: true, summary: "Those are already the channel's settings." }
        const routers = await routersOf([a.id])
        if (after.type !== before.type && routers.length && !ROUTED_TYPES.includes(after.type)) {
          return { error: `Routers ${routers.map(r => r.id).join(", ")} point at this channel, and routers only reach Meta channels. Delete them first, or keep a Meta type.` }
        }
        const ok = await ctx.ask({
          summary: `Change channel "${before.title}"${changed.includes("agent") ? " (changes which agent answers live messages)" : ""}`,
          before: summarize(before),
          after: summarize(after),
        })
        if (!ok) return { error: "Rejected by reviewer." }
        const patch = Object.fromEntries(changed.map(k => [k, after[k] ?? null]))
        const { data, error } = await supabase.from("channels").update(patch as any).eq("id", a.id).eq("team", team).select("*").single()
        if (error) return { error: error.message }
        announce({ table: "channels", id: a.id })
        const titles = await agentTitles([after.agent])
        return { ok: true, changed, channel: describeChannel(data, titles.get(after.agent), routers) }
      },
    }),

    channel_delete: tool({
      description: "Delete one channel. Messages to it stop being answered. Its routers must be deleted first, one by one. Always asks the user first.",
      parameters: z.object({ id: z.string() }),
      execute: async ({ id }) => {
        if (!team) return noTeam
        const c = await loadChannel(id)
        if (!c) return { error: `No channel ${id} in this team.` }
        const routers = await routersOf([id])
        if (routers.length) {
          return { error: `Routers still point at this channel: ${routers.map(r => `"${r.title}" (${r.id})`).join(", ")}. Delete each with router_delete first.` }
        }
        const ok = await ctx.ask({
          summary: `Delete ${TYPE_LABEL[c.type] ?? c.type} channel "${c.title}"`,
          detail: summarize(c) + "\n\nMessages to this channel stop being answered. This can't be undone.",
        })
        if (!ok) return { error: "Rejected by reviewer." }
        const { error } = await supabase.from("channels").delete().eq("id", id).eq("team", team)
        if (error) return { error: error.message }
        announce({ table: "channels", id, deleted: true })
        return { ok: true, deleted: id }
      },
    }),

    channel_connect_telegram: tool({
      description: "Point a Telegram channel's bot at its webhook (Telegram setWebhook), using the token saved on the channel. Replaces the bot's current webhook. Always asks the user first.",
      parameters: z.object({
        id: z.string(),
        environment: z.enum(["prod", "qa"]).describe("prod for the live app, qa for testing"),
      }),
      execute: async ({ id, environment }) => {
        if (!team) return noTeam
        const c = await loadChannel(id)
        if (!c) return { error: `No channel ${id} in this team.` }
        if (c.type !== "telegram") return { error: `"${c.title}" isn't a Telegram channel.` }
        const token = c.settings?.bot_token
        if (!token) return { error: "The channel has no bot token. Set botToken with channel_update first." }
        const url = channelWebhooks(id)[environment]
        const ok = await ctx.ask({
          summary: `Point the Telegram bot of "${c.title}" at the ${environment === "prod" ? "production" : "QA"} webhook`,
          detail: `${url}\n\nThe bot stops delivering to wherever it pointed before.`,
        })
        if (!ok) return { error: "Rejected by reviewer." }
        try {
          const res = await fetch(`https://api.telegram.org/bot${token}/setWebhook?url=${encodeURIComponent(url)}`)
          const body = await res.json()
          if (!body?.ok) return { error: `Telegram refused: ${body?.description ?? res.status}` }
          return { ok: true, webhook: url }
        } catch (e: any) {
          return { error: `Couldn't reach Telegram (${e?.message ?? e}). The user can open https://api.telegram.org/bot<their bot token>/setWebhook?url=${encodeURIComponent(url)} themselves.` }
        }
      },
    }),

    channel_telegram_status: tool({
      description: "Where a Telegram channel's bot delivers messages now, and its last delivery error (Telegram getWebhookInfo).",
      parameters: z.object({ id: z.string() }),
      execute: async ({ id }) => {
        if (!team) return noTeam
        const c = await loadChannel(id)
        if (!c) return { error: `No channel ${id} in this team.` }
        const token = c.settings?.bot_token
        if (c.type !== "telegram" || !token) return { error: "Not a Telegram channel with a bot token." }
        try {
          const res = await fetch(`https://api.telegram.org/bot${token}/getWebhookInfo`)
          const body = await res.json()
          if (!body?.ok) return { error: `Telegram refused: ${body?.description ?? res.status}` }
          const info = body.result ?? {}
          const hooks = channelWebhooks(id)
          return {
            webhook: info.url || null,
            pointsHere: info.url === hooks.prod ? "prod" : info.url === hooks.qa ? "qa" : false,
            pendingUpdates: info.pending_update_count ?? 0,
            lastError: info.last_error_message ?? null,
            lastErrorAt: info.last_error_date ? new Date(info.last_error_date * 1000).toISOString() : null,
          }
        } catch (e: any) {
          return { error: `Couldn't reach Telegram: ${e?.message ?? e}` }
        }
      },
    }),

    routers_list: tool({
      description: "List this team's Meta routers: which Page, Instagram account or WhatsApp number (asset id) reaches which channel.",
      parameters: z.object({ channelId: z.string().nullable().describe("Only routers for this channel; null for all") }),
      execute: async ({ channelId }) => {
        if (!team) return noTeam
        let q = supabase.from("routers").select("*, channel (id, title, type)").eq("team", team)
        if (channelId) q = q.eq("channel", channelId)
        const { data, error } = await q.order("created_at", { ascending: false })
        if (error) return { error: error.message }
        return {
          sharedWebhook: ROUTER_WEBHOOK,
          routers: (data ?? []).map((r: any) => ({
            id: r.id, title: r.title, assetId: r.asset_id,
            channel: r.channel ? { id: r.channel.id, title: r.channel.title, type: r.channel.type } : null,
            page: `/settings/routers/${r.id}`,
          })),
        }
      },
    }),

    router_create: tool({
      description:
        "Route a Meta asset to a WhatsApp, Messenger or Instagram channel through the shared webhook. The asset id is: "
        + Object.entries(ASSET_HINT).map(([t, h]) => `${t}: ${h}`).join("; ") + ". Always asks the user first.",
      parameters: z.object({
        title: z.string(),
        assetId: z.string(),
        channelId: z.string(),
      }),
      execute: async ({ title, assetId, channelId }) => {
        if (!team) return noTeam
        const asset = assetId.trim()
        if (!title.trim() || !asset) return { error: "title and assetId are required." }
        const { channel, error: channelError } = await routableChannel(channelId)
        if (channelError) return { error: channelError }
        const taken = await assetTaken(asset)
        if (taken) return { error: taken }
        const ok = await ctx.ask({
          summary: `Route ${TYPE_LABEL[channel.type]} asset ${asset} to "${channel.title}"`,
          detail: `Router: ${title.trim()}\nAsset id (${ASSET_HINT[channel.type]}): ${asset}\n\nMessages to that asset reach this channel's agent.`,
        })
        if (!ok) return { error: "Rejected by reviewer." }
        const { data, error } = await supabase.from("routers").insert({ title: title.trim(), asset_id: asset, channel: channelId, team }).select("*").single()
        if (error) return { error: uniqueViolation(error) ? `Asset ${asset} is already routed by another router.` : error.message }
        announce({ table: "routers", id: (data as any).id })
        return {
          ok: true,
          router: { id: (data as any).id, title: title.trim(), assetId: asset, channelId, page: `/settings/routers/${(data as any).id}` },
          note: `Meta must deliver this asset's webhooks to the shared router url (${ROUTER_WEBHOOK.prod}; QA ${ROUTER_WEBHOOK.qa}): the asset has to be connected to the Signpost Meta app.`,
        }
      },
    }),

    router_update: tool({
      description: "Change a router's title, asset id or channel. Only the fields you pass change. Always asks the user first.",
      parameters: z.object({
        id: z.string(),
        title: z.string().nullable(),
        assetId: z.string().nullable(),
        channelId: z.string().nullable(),
      }),
      execute: async ({ id, title, assetId, channelId }) => {
        if (!team) return noTeam
        const before = await loadRouter(id)
        if (!before) return { error: `No router ${id} in this team.` }
        const after = {
          title: title?.trim() || before.title,
          asset_id: assetId?.trim() || before.asset_id,
          channel: channelId || before.channel,
        }
        const { channel, error: channelError } = await routableChannel(after.channel)
        if (channelError) return { error: channelError }
        if (after.asset_id !== before.asset_id) {
          const taken = await assetTaken(after.asset_id, id)
          if (taken) return { error: taken }
        }
        const show = (r: any, ch?: any) => `Router: ${r.title}\nAsset id: ${r.asset_id}\nChannel: ${ch ? `${ch.title} (${ch.id})` : r.channel}`
        const prevChannel = before.channel === after.channel ? channel : await loadChannel(before.channel)
        const ok = await ctx.ask({ summary: `Change router "${before.title}"`, before: show(before, prevChannel), after: show(after, channel) })
        if (!ok) return { error: "Rejected by reviewer." }
        const { error } = await supabase.from("routers").update(after).eq("id", id).eq("team", team)
        if (error) return { error: uniqueViolation(error) ? `Asset ${after.asset_id} is already routed by another router.` : error.message }
        announce({ table: "routers", id })
        return { ok: true, router: { id, title: after.title, assetId: after.asset_id, channelId: after.channel } }
      },
    }),

    router_delete: tool({
      description: "Delete one router. Messages to its asset stop reaching the channel. Always asks the user first.",
      parameters: z.object({ id: z.string() }),
      execute: async ({ id }) => {
        if (!team) return noTeam
        const r = await loadRouter(id)
        if (!r) return { error: `No router ${id} in this team.` }
        const ok = await ctx.ask({
          summary: `Delete router "${r.title}"`,
          detail: `Asset id: ${r.asset_id}\nChannel: ${r.channel}\n\nMessages to this asset stop being answered.`,
        })
        if (!ok) return { error: "Rejected by reviewer." }
        const { error } = await supabase.from("routers").delete().eq("id", id).eq("team", team)
        if (error) return { error: error.message }
        announce({ table: "routers", id, deleted: true })
        return { ok: true, deleted: id }
      },
    }),
  }

  return tools
}
